
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

import loadable from '@loadable/component';

import { Water, DropletFill, ClockHistory, ArrowRightCircleFill, ThermometerHalf, GraphUp } from 'react-bootstrap-icons';
import Badge from 'react-bootstrap/Badge';

import StationNameRenderer from '../StationNameRenderer.js';

import CentralLink from '../CentralLink.js';

import CommonHeaders from '../helmet-headers/CommonHeaders.js';

import { formatTimestamp } from '../../lib/formatHelpers.js';

import MetricRecentTrend from '../MetricRecentTrend.js';

const WaterLevelGauge = loadable(() => import('../WaterLevelGauge.js'));
const WaterLevelLineChart = loadable(() => import('../WaterLevelLineChart.js'));
const GoverningBodyName = loadable(() => import('../GoverningBodyName.js'));

const FloodEventHistoryButton = loadable(() => import('../FloodEventHistoryButton.js'));

const RenderMonthlyLevelChart = loadable(() => import('../RenderMonthlyLevelChart.js'));

const RenderStationLevelBased = function({ isMissingMetrics, station, qualifierType, computedQualifierType }) {

	let computedStationName = station && <StationNameRenderer stationName={station.stationName} qualifierType={computedQualifierType} />;

	let roundedRangeLow;
	let roundedRangeHigh;
	let roundedRiverHeight;
	let roundedFlowHeight;
	let typicalRangeText;
	let maxOnRecord, maxOnRecordTimestampLocale;

	let riverName = station && station.riverName ? station.riverName : null;
	// let stationName = station && station.eaData && station.eaData.eaStationLabel;
	let stationName = station && station.stationName;
	let townName = station && station.placeName;

	let riverOrTide = computedQualifierType === 'Tide' ? 'tide' : 'river';

	let computedTitle = null;
	let townNameTitleSuffix = !!townName ? " in " + townName : "";
	if(stationName) {
		computedTitle = "The " + riverOrTide + " level at the " + stationName + " station" + townNameTitleSuffix;
		if(riverName) {
			computedTitle = "The " + riverName + " level at the " + stationName + " station" + townNameTitleSuffix;
		}
	} else if(riverName) {
		computedTitle = "The " + riverOrTide + " level for the " + riverName + townNameTitleSuffix;
	}

	if(station) {
		roundedRiverHeight = (station.currentHeight && Math.round(100*station.currentHeight)/100) || null;
		roundedFlowHeight = (station.currentFlow && Math.round(100*station.currentFlow)/100) || null;
		
		if(station.metadata) {
			maxOnRecord = station.metadata.maxOnRecord;
			maxOnRecordTimestampLocale = formatTimestamp(station.metadata.maxOnRecordTimestamp);
		}
	}

	let waterLevelGaugeRender = null;
	if(station && station.metadata) {
		waterLevelGaugeRender = <WaterLevelGauge
		  maxOnRecord={station.metadata.maxOnRecord}
		  minOnRecord={station.metadata.minOnRecord}
		  typicalRangeHigh={station.metadata.typicalRangeHigh}
		  typicalRangeLow={station.metadata.typicalRangeLow}
		  currentValue={station.currentHeight} // Current value for demonstration
		  gaugeLabel={stationName}
		  forecastValue={station.forecastHeight}
		/>;
	}

	let currentTemperature2DP = null;
	if(station && station.currentWaterTemperature && !isNaN(parseFloat(station.currentWaterTemperature))) {
		currentTemperature2DP = parseFloat(station.currentWaterTemperature).toFixed(2);
	}

	//Check the latest {{ eaStation.riverName }} levels and flood warnings at the {{ stationLabel }} measuring station. View local data and stay informed with real-time updates and alerts.
	let metaDescription = "Check the latest " + (riverOrTide === 'tide' ? riverOrTide : (station.riverName ? station.riverName : riverOrTide)) + " levels and flood warnings at the " + stationName + " measuring station" + townNameTitleSuffix + ". View local data and stay informed with real-time updates and alerts.";


	let change15 = null, change60 = null, change180 = null;

	if (station && station.currentHeightTimestamp && station.history && Array.isArray(station.history)) {
	  let now = new Date(station.currentHeightTimestamp).getTime();

	  let find15 = station.history.find(function (historyEntry) {
	    // Convert entryTimestamp to milliseconds and compare
	    return Math.abs(new Date(historyEntry.entryTimestamp).getTime() - (now - 15 * 60 * 1000)) < 1000;
	  });
	  let find60 = station.history.find(function (historyEntry) {
	    return Math.abs(new Date(historyEntry.entryTimestamp).getTime() - (now - 60 * 60 * 1000)) < 1000;
	  });
	  let find180 = station.history.find(function (historyEntry) {
	    return Math.abs(new Date(historyEntry.entryTimestamp).getTime() - (now - 180 * 60 * 1000)) < 1000;
	  });

	  if (find15) {
	    change15 = station.currentHeight - find15.entryHeight;
	  }
	  if (find60) {
	    change60 = station.currentHeight - find60.entryHeight;
	  }
	  if (find180) {
	    change180 = station.currentHeight - find180.entryHeight;
	  }
	}

	let minByMonth = [], maxByMonth = [], meanByMonth = [];
	if(station && station.metadata) {
		minByMonth = [
			station.metadata.minJan,
			station.metadata.minFeb,
			station.metadata.minMar,
			station.metadata.minApr,
			station.metadata.minMay,
			station.metadata.minJun,
			station.metadata.minJul,
			station.metadata.minAug,
			station.metadata.minSep,
			station.metadata.minOct,
			station.metadata.minNov,
			station.metadata.minDec,
		];
		meanByMonth = [
			station.metadata.meanJan,
			station.metadata.meanFeb,
			station.metadata.meanMar,
			station.metadata.meanApr,
			station.metadata.meanMay,
			station.metadata.meanJun,
			station.metadata.meanJul,
			station.metadata.meanAug,
			station.metadata.meanSep,
			station.metadata.meanOct,
			station.metadata.meanNov,
			station.metadata.meanDec,
		];
		maxByMonth = [
			station.metadata.maxJan,
			station.metadata.maxFeb,
			station.metadata.maxMar,
			station.metadata.maxApr,
			station.metadata.maxMay,
			station.metadata.maxJun,
			station.metadata.maxJul,
			station.metadata.maxAug,
			station.metadata.maxSep,
			station.metadata.maxOct,
			station.metadata.maxNov,
			station.metadata.maxDec,
		];
	}


	let levelHistoricalChart = null;
	if(station && station.metadata && station.metadata.minJun) {
		levelHistoricalChart = [
			<Col key="sn_h_mrainfall" xs={12} className="header-bold bg-secondary text-white"><h3 className="hX-blendin">Seasonal Groundwater Level Trends</h3></Col>,
		]
		if(station.metadata.avgHistoryStartYear && station.metadata.avgHistoryEndYear) {
			levelHistoricalChart.push(
				<Col key="sn_d_mrainfall" xs={12} style={{fontSize: "0.7em"}}>
					<i><b>Calculation Period</b>: {station.metadata.avgHistoryStartYear}-{station.metadata.avgHistoryEndYear}</i>
				</Col>
			);
		}
		levelHistoricalChart.push(
			<Col key="sn_b_mrainfall" xs={12}>
		    	<RenderMonthlyLevelChart minByMonth={minByMonth} maxByMonth={maxByMonth} meanByMonth={meanByMonth} />
		    </Col>
		);
	}

	const floodingOrGroundwater = computedQualifierType === "Groundwater Level" ? "groundwater" : "flooding";


	return <div>
			<CommonHeaders
				title={computedTitle}
				description={metaDescription}
			/>
				<Row>
					<Col className="d-none d-md-block" md={1} lg={1} />
					<Col xs={4} md={3} lg={2}>
						{!isMissingMetrics ? <div style={{height: waterLevelGaugeRender ? "280px" : "100%", display: "flex", justifyContent: "center", alignItems: "center"}}>
							{waterLevelGaugeRender || <div><b><DropletFill /></b><br /><i>No gauge for this station</i></div>}
						</div> : null}
					</Col>
					<Col xs={8} md={7} lg={6}>
						<Row>
							<Col xs={12}><center><strong className="hX-scale20em"><Badge pill bg="dark"><Water /> {(roundedRiverHeight && (roundedRiverHeight + "m")) || "No Data"}</Badge></strong></center></Col>
						</Row>
						<Row>
							{station && roundedRiverHeight ? <Col xs={12}>
								Is the {station.riverName ? <span><strong>{station.riverName.replace(/\s*level$/i, '')}</strong></span> : "current "+riverOrTide} level at the <strong><i>{computedStationName} station</i></strong>{townName ? <span> in <strong>{townName}</strong></span> : ""}, as of our last update, at <b><i>{formatTimestamp(station.currentHeightTimestamp)}</i></b>.
							</Col> : null}
							{station && roundedRiverHeight ? <Row className="mt-2">
					<Row>
						<Col xs={12}>
							{change180 !== null ? <span>The {station.riverName ? <span><strong>{station.riverName}</strong></span> : 'river'} has been <MetricRecentTrend timeframe={180} entryHeightChange={change180} shortSummary={true} /> over the last <b>3 hour</b> period.</span> : null}
						</Col>
						<Col xs={12} className="d-lg-none">
							{change15 || change60 || change180 ? <Row>
								{change15 !== null ? <Col xs={12} className="mt-1"><MetricRecentTrend timeframe={15} entryHeightChange={change15} /></Col> : null}
								{change60 !== null ? <Col xs={12} className="mt-1"><MetricRecentTrend timeframe={60} entryHeightChange={change60} /></Col> : null}
								{change180 !== null ? <Col xs={12} className="mt-1"><MetricRecentTrend timeframe={180} entryHeightChange={change180} /></Col> : null}
							</Row> : null}
						</Col>
					</Row>
				</Row> : null}
							{station && !roundedRiverHeight ? <Col xs={12}>
								{!isMissingMetrics ? <>
									We do not currently have live level data for this station and have been unable to collect any for the previous week.<br />
									The station may be offline for maintenance or we may have had an issue collecting the data.</> : <>We do not currently collect any metrics for this station.</>}
							</Col> : null}
							{!station ? <Col xs={12}>We are loading the station data</Col> : null}
						</Row>
						{station && roundedFlowHeight ? <Row>
							<Col xs={12}><center><strong className="hX-scale12em"><Badge pill bg="dark"><ArrowRightCircleFill /> {roundedFlowHeight ? roundedFlowHeight.toLocaleString() : null} m3/sec</Badge></strong></center></Col>
							<Col xs={12}>Is the flow level here for the {station.riverName ? <b>{station.riverName}</b> : 'station'}, recorded at <b>{formatTimestamp(station.currentFlowTimestamp)}</b>.</Col>
						</Row> : null}
						{station && station.currentWaterTemperature ? <Row>
							<Col xs={12}>
								<center><strong className="hX-scale12em"><Badge pill bg="dark" size="sm"><ThermometerHalf /> {currentTemperature2DP} <span><super>&deg;</super>C</span></Badge></strong></center></Col>
							<Col xs={12}>Is the water temperature here, for the {station.riverName ? <b>{station.riverName}</b> : 'station'}, recorded at <b>{formatTimestamp(station.currentWaterTemperatureTimestamp)}</b>.</Col>
						</Row> : null}
						{(station && station.metadata && station.metadata.floodEventCount && station.metadata.floodEventCount > 0) ? <FloodEventHistoryButton
							floodEventCount={station.metadata.floodEventCount}
							earliestFloodEventTime={station.metadata.earliestFloodEventTime}
							stationId={station.stationId}
							urlSlug={station.urlSlug}
						/> : null}
					</Col>
					<Col className="d-none d-md-block d-flex align-items-center" lg={2}>
					  {change15 !== null || change60 !== null || change180 !== null ? (
					    <Row className="d-flex align-items-center h-100">
					      <Col xs={12} className=" d-none d-lg-inline">
					      	<Row>
					      		<Col xs={12}><b><GraphUp/><span className="d-none d-xl-inline" > Recent</span> Trends</b></Col>
						      	{change15 !== null ? (
						        <Col xs={12} className="mt-1">
						          <MetricRecentTrend timeframe={15} entryHeightChange={change15} />
						        </Col>
						      ) : null}
						      {change60 !== null ? (
						        <Col xs={12} className="mt-1">
						          <MetricRecentTrend timeframe={60} entryHeightChange={change60} />
						        </Col>
						      ) : null}
						      {change180 !== null ? (
						        <Col xs={12} className="mt-1">
						          <MetricRecentTrend timeframe={180} entryHeightChange={change180} />
						        </Col>
						      ) : null}
						    </Row>
						  </Col>
					    </Row>
					  ) : null}
					</Col>
					<Col className="d-none d-md-block" md={1} lg={1} />
				</Row>
				<Row>
					<Col xs={12}>
						{(station && !isMissingMetrics) ? <WaterLevelLineChart
							history={station.history}
							forecast={station.forecast}
							historicalMaximum={maxOnRecord}
						/> : null}
					</Col>
					{(station && station.forecast && station.forecast.length > 0) ? <Col xs={12}><i>This station includes an automated forecast model, forecasts are shown above in the grey area.</i></Col> : null}
				</Row>
				{levelHistoricalChart}
				{(station && !isMissingMetrics) ? <Row>
					<Col xs={12} className="bg-dark text-white"><h3 className="hX-blendin header-bold">{computedStationName} - Information Summary</h3></Col>
					{maxOnRecord && station ? <Col xs={12} md={12}>
						<strong>What is the highest level of {floodingOrGroundwater}{townNameTitleSuffix} at the {computedStationName} station?</strong><br />
						The maximum on record for the <i>{computedStationName}</i> station{townNameTitleSuffix} on the {station.riverName ? station.riverName : "river"} is <strong>{maxOnRecord}m</strong>{maxOnRecordTimestampLocale && <span>, which was at <strong>{maxOnRecordTimestampLocale}</strong></span>}.
					</Col> : null}
					{typicalRangeText && <Col xs={12} md={6}>
						<strong>What is the normal range for the {riverName}{townNameTitleSuffix}?</strong><br />
						<span>{typicalRangeText}</span>
					</Col>}
				</Row> : null}
			</div>;
}

export default RenderStationLevelBased;